import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import {
    BigModal,
    FlexDiv,
    Heading3,
    TextDefault,
    TextTiny,
    ThemedButton,
    palette,
} from '@awarego-ui/components';
import pluralize from 'pluralize';
import ThemedSpinner from '../../../../components/themed/ThemedSpinner';
import services from '../../../../services';
import { useStores } from '../../../../hooks/use-stores';
import { Link } from 'react-router-dom';
import RecommendationTrainingConfigModal, {
    parseRecommendationTrainingConfig,
    scheduleTypeLabel,
} from '../../../settings/RecommendationTrainingConfigModal';

function RecommendationTrainingGroupUsersModal({
    open,
    onClose,
    loading,
    group,
    threshold,
}) {
    const users = group?.users || [];

    return (
        <BigModal
            isOpen={open}
            onRequestClose={onClose}
            heading={group?.name || 'Employees'}
            modalContent={
                loading ? (
                    <ThemedSpinner />
                ) : (
                    <FlexDiv column gap={16}>
                        <TextDefault lighter>
                            {users.length}{' '}
                            {pluralize('employee', users.length)}
                            {group?.notCompleted
                                ? ' who have not completed the assessment'
                                : group?.failedAreaPattern?.length
                                  ? ` below ${threshold}% in: ${group.failedAreaPattern.join(
                                        ', '
                                    )}`
                                  : ''}
                        </TextDefault>
                        <div
                            style={{
                                maxHeight: 420,
                                overflow: 'auto',
                                border: `1px solid ${palette.graphite.cloud}`,
                                borderRadius: 8,
                            }}
                        >
                            <table
                                style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                }}
                            >
                                <thead>
                                    <tr
                                        style={{
                                            textAlign: 'left',
                                            background: palette.graphite.cloud,
                                        }}
                                    >
                                        <th style={{ padding: '8px 12px' }}>
                                            <TextTiny bold>Name</TextTiny>
                                        </th>
                                        <th style={{ padding: '8px 12px' }}>
                                            <TextTiny bold>Email</TextTiny>
                                        </th>
                                        <th style={{ padding: '8px 12px' }}>
                                            <TextTiny bold>Weak areas</TextTiny>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((u) => (
                                        <tr
                                            key={u.email}
                                            style={{
                                                borderTop: `1px solid ${palette.graphite.cloud}`,
                                            }}
                                        >
                                            <td style={{ padding: '8px 12px' }}>
                                                <TextDefault>
                                                    {u.name || '—'}
                                                </TextDefault>
                                            </td>
                                            <td style={{ padding: '8px 12px' }}>
                                                <TextDefault>
                                                    {u.email}
                                                </TextDefault>
                                            </td>
                                            <td style={{ padding: '8px 12px' }}>
                                                <TextDefault lighter>
                                                    {u.notCompleted
                                                        ? 'Not completed'
                                                        : (
                                                              u.failedAreas ||
                                                              []
                                                          ).join(', ') || '—'}
                                                </TextDefault>
                                            </td>
                                        </tr>
                                    ))}
                                    {users.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={3}
                                                style={{
                                                    padding: '16px 12px',
                                                }}
                                            >
                                                <TextDefault lighter>
                                                    No employees in this group.
                                                </TextDefault>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </FlexDiv>
                )
            }
            modalFooterContent={
                <ThemedButton
                    mode="secondary"
                    text="Close"
                    onClick={onClose}
                    $noTextTransform
                />
            }
        />
    );
}

function RecommendationTrainingGroups({
    companyId,
    assessmentId,
    listId = 'all',
    onSubjectClick,
}) {
    const { commonStore, store } = useStores();
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [usersModalOpen, setUsersModalOpen] = useState(false);
    const [usersLoading, setUsersLoading] = useState(false);
    const [usersGroup, setUsersGroup] = useState(null);
    const [configModalOpen, setConfigModalOpen] = useState(false);
    const [configOverride, setConfigOverride] = useState(null);

    const listParam = listId === 'all' ? null : listId;

    const companyTrainingDraft = useMemo(
        () =>
            parseRecommendationTrainingConfig(
                store.currentCompanyUISettings || {}
            ),
        [store.currentCompanyUISettings]
    );

    const effectiveConfigDraft = useMemo(() => {
        if (!configOverride) return companyTrainingDraft;
        return {
            nameTemplate:
                configOverride.nameTemplate ||
                companyTrainingDraft.nameTemplate,
            listNameTemplate:
                configOverride.listNameTemplate ||
                companyTrainingDraft.listNameTemplate,
            dueDays: configOverride.dueDays ?? companyTrainingDraft.dueDays,
            sendingTime:
                configOverride.sendingTime || companyTrainingDraft.sendingTime,
            scheduleType:
                configOverride.scheduleType ??
                companyTrainingDraft.scheduleType,
            remindersEnabled: !!configOverride.remindersEnabled,
            remindersChannel:
                typeof configOverride.remindersSettings?.channel === 'number'
                    ? configOverride.remindersSettings.channel
                    : companyTrainingDraft.remindersChannel,
            remindersSteps: Array.isArray(
                configOverride.remindersSettings?.steps
            )
                ? configOverride.remindersSettings.steps.map((s) => ({ ...s }))
                : [],
        };
    }, [companyTrainingDraft, configOverride]);

    const loadGroups = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await services.Companies.assessmentsService(
                companyId
            )
                .recommendationsService(assessmentId)
                .trainingGroups(listParam ? { listId: listParam } : undefined);
            setData(result);
            const creatable = (result.trainings || []).filter(
                (t) => !t.existingTraining
            );
            setSelectedIds(creatable.map((t) => t.id));
        } catch (e) {
            setError(
                e?.response?.data?.message ||
                    e?.message ||
                    'Failed to load training groups'
            );
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            if (!companyId || !assessmentId) return;
            setLoading(true);
            setError(null);
            try {
                const result = await services.Companies.assessmentsService(
                    companyId
                )
                    .recommendationsService(assessmentId)
                    .trainingGroups(
                        listParam ? { listId: listParam } : undefined
                    );
                if (!cancelled) {
                    setData(result);
                    const creatable = (result.trainings || []).filter(
                        (t) => !t.existingTraining
                    );
                    setSelectedIds(creatable.map((t) => t.id));
                }
            } catch (e) {
                if (!cancelled) {
                    setError(
                        e?.response?.data?.message ||
                            e?.message ||
                            'Failed to load training groups'
                    );
                    setData(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [companyId, assessmentId, listParam]);

    const trainings = data?.trainings || [];
    const creatableTrainings = trainings.filter((t) => !t.existingTraining);
    const allSelected =
        creatableTrainings.length > 0 &&
        selectedIds.length === creatableTrainings.length &&
        creatableTrainings.every((t) => selectedIds.includes(t.id));

    const toggleAll = () => {
        if (allSelected) setSelectedIds([]);
        else setSelectedIds(creatableTrainings.map((t) => t.id));
    };

    const toggleOne = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const openUsers = async (group) => {
        setUsersModalOpen(true);
        setUsersGroup({
            ...group,
            users: group.users || [],
        });
        // Prefer embedded users from training-groups; refetch if missing.
        if (group.users?.length) {
            setUsersLoading(false);
            return;
        }
        setUsersLoading(true);
        try {
            const result = await services.Companies.assessmentsService(
                companyId
            )
                .recommendationsService(assessmentId)
                .trainingGroupUsers(
                    group.id,
                    listParam ? { listId: listParam } : undefined
                );
            setUsersGroup({
                ...group,
                ...(result || {}),
                users: result?.users || [],
            });
        } catch (e) {
            commonStore.error(
                e?.response?.data?.message ||
                    e?.message ||
                    'Failed to load employees'
            );
        } finally {
            setUsersLoading(false);
        }
    };

    const createTrainings = async (groupIds) => {
        setCreating(true);
        try {
            const result = await services.Companies.assessmentsService(
                companyId
            )
                .recommendationsService(assessmentId)
                .createTrainingsFromGroups({
                    groupIds,
                    ...(listParam ? { listId: listParam } : {}),
                    ...(configOverride
                        ? { trainingConfig: configOverride }
                        : {}),
                });
            commonStore.success(
                `Created ${result.trainings?.length || 0} ${pluralize(
                    'training',
                    result.trainings?.length || 0
                )}`
            );
            await loadGroups();
        } catch (e) {
            commonStore.error(
                e?.response?.data?.message ||
                    e?.message ||
                    'Failed to create trainings'
            );
        } finally {
            setCreating(false);
        }
    };

    const selectedCount = useMemo(() => {
        return trainings
            .filter((t) => selectedIds.includes(t.id))
            .reduce((sum, t) => sum + (t.userCount || 0), 0);
    }, [trainings, selectedIds]);

    if (loading) {
        return (
            <FlexDiv mt={32} justifyCenter>
                <ThemedSpinner />
            </FlexDiv>
        );
    }

    if (error) {
        return (
            <FlexDiv mt={32}>
                <TextDefault color={palette.vibrant.raspberry}>
                    {error}
                </TextDefault>
            </FlexDiv>
        );
    }

    if (!data?.multiTraining || trainings.length === 0) {
        return null;
    }

    const summaryDue = effectiveConfigDraft.dueDays || 30;
    const summaryReminders = effectiveConfigDraft.remindersEnabled
        ? 'On'
        : 'Off';

    return (
        <FlexDiv column mt={40} gap={16}>
            <FlexDiv
                column
                gap={8}
                style={{
                    padding: 16,
                    background: palette.graphite.cloud,
                    borderRadius: 8,
                }}
            >
                <FlexDiv justifyBetween alignCenter gap={16} $noWrap>
                    <FlexDiv column gap={4}>
                        <TextDefault bold>
                            Training settings
                            {configOverride ? ' (override)' : ''}
                        </TextDefault>
                        <TextTiny lighter>
                            Name: {effectiveConfigDraft.nameTemplate} · List:{' '}
                            {effectiveConfigDraft.listNameTemplate} · Send:{' '}
                            {scheduleTypeLabel(
                                effectiveConfigDraft.scheduleType
                            )}{' '}
                            · Due: {summaryDue} days · Reminders:{' '}
                            {summaryReminders}
                        </TextTiny>
                        <TextTiny lighter>
                            Applies to all trainings created from this page.
                            Company defaults are in Content settings.
                        </TextTiny>
                    </FlexDiv>
                    <ThemedButton
                        mode="secondary"
                        size="tiny"
                        $noTextTransform
                        text="Configure"
                        onClick={() => setConfigModalOpen(true)}
                    />
                </FlexDiv>
            </FlexDiv>

            <FlexDiv justifyBetween alignCenter gap={16} $noWrap>
                <FlexDiv column gap={4}>
                    <Heading3>Proposed trainings</Heading3>
                    <TextDefault lighter>
                        Grouped by failed threat areas (threshold{' '}
                        {data.threshold ?? '—'}
                        %). Each employee appears in exactly one training.{' '}
                        {data.totalEligibleUsers}{' '}
                        {pluralize(
                            'eligible employee',
                            data.totalEligibleUsers
                        )}
                        .
                    </TextDefault>
                </FlexDiv>
                <ThemedButton
                    mode="primary"
                    text="Create training(s)"
                    disabled={creating || selectedIds.length === 0}
                    onClick={() => createTrainings(selectedIds)}
                    $saving={creating}
                    $noTextTransform
                />
            </FlexDiv>

            <div
                style={{
                    border: `1px solid ${palette.graphite.cloud}`,
                    borderRadius: 8,
                    overflow: 'hidden',
                }}
            >
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr
                            style={{
                                textAlign: 'left',
                                background: palette.graphite.cloud,
                            }}
                        >
                            <th style={{ padding: '10px 12px', width: 40 }}>
                                <input
                                    type="checkbox"
                                    checked={allSelected}
                                    onChange={toggleAll}
                                    disabled={creatableTrainings.length === 0}
                                    aria-label="Select all trainings"
                                />
                            </th>
                            <th style={{ padding: '10px 12px' }}>
                                <TextTiny bold>Training</TextTiny>
                            </th>
                            <th style={{ padding: '10px 12px' }}>
                                <TextTiny bold>Threat areas</TextTiny>
                            </th>
                            <th style={{ padding: '10px 12px' }}>
                                <TextTiny bold>Subjects</TextTiny>
                            </th>
                            <th style={{ padding: '10px 12px', width: 140 }}>
                                <TextTiny bold>Users</TextTiny>
                            </th>
                            <th style={{ padding: '10px 12px', width: 160 }}>
                                <TextTiny bold>Status</TextTiny>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {trainings.map((t) => (
                            <tr
                                key={t.id}
                                style={{
                                    borderTop: `1px solid ${palette.graphite.cloud}`,
                                    verticalAlign: 'top',
                                }}
                            >
                                <td style={{ padding: '12px' }}>
                                    {t.existingTraining ? (
                                        <span aria-hidden="true">—</span>
                                    ) : (
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(t.id)}
                                            onChange={() => toggleOne(t.id)}
                                            aria-label={`Select ${t.name}`}
                                        />
                                    )}
                                </td>
                                <td style={{ padding: '12px' }}>
                                    <TextDefault bold>
                                        {`Training ${t.index}`}
                                    </TextDefault>
                                </td>
                                <td style={{ padding: '12px' }}>
                                    {t.notCompleted ? (
                                        <TextDefault lighter>
                                            Not completed
                                        </TextDefault>
                                    ) : (
                                        <FlexDiv column gap={2}>
                                            {(t.failedAreaPattern || []).map(
                                                (area) => (
                                                    <TextDefault key={area}>
                                                        {area}
                                                    </TextDefault>
                                                )
                                            )}
                                            {(t.unmappedAreas || []).length >
                                                0 && (
                                                <TextTiny
                                                    color={
                                                        palette.vibrant
                                                            .raspberry
                                                    }
                                                >
                                                    No subject mapped:{' '}
                                                    {t.unmappedAreas.join(', ')}
                                                </TextTiny>
                                            )}
                                        </FlexDiv>
                                    )}
                                </td>
                                <td style={{ padding: '12px' }}>
                                    <FlexDiv column gap={4}>
                                        {(t.subjects || []).length === 0 && (
                                            <TextDefault lighter>
                                                —
                                            </TextDefault>
                                        )}
                                        {(t.subjects || []).map((s) => (
                                            <button
                                                key={s.id}
                                                type="button"
                                                onClick={() =>
                                                    onSubjectClick?.(s.id)
                                                }
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: 0,
                                                    textAlign: 'left',
                                                    cursor: 'pointer',
                                                    color: palette.evergreen
                                                        .aspargus,
                                                }}
                                            >
                                                <TextDefault>
                                                    {s.title ||
                                                        `Subject ${s.id}`}
                                                </TextDefault>
                                            </button>
                                        ))}
                                    </FlexDiv>
                                </td>
                                <td style={{ padding: '12px' }}>
                                    <button
                                        type="button"
                                        onClick={() => openUsers(t)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            padding: 0,
                                            cursor: 'pointer',
                                            color: palette.evergreen.aspargus,
                                            textDecoration: 'underline',
                                        }}
                                    >
                                        <TextDefault bold>
                                            {t.userCount}{' '}
                                            {pluralize('user', t.userCount)}
                                        </TextDefault>
                                    </button>
                                </td>
                                <td style={{ padding: '12px' }}>
                                    {t.existingTraining ? (
                                        <Link
                                            to={`/trainings/${t.existingTraining.id}/view`}
                                        >
                                            <TextDefault>
                                                {t.existingTraining.name ||
                                                    'Open training'}
                                            </TextDefault>
                                        </Link>
                                    ) : (
                                        <TextTiny lighter>Not created</TextTiny>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {selectedIds.length > 0 &&
                selectedIds.length < creatableTrainings.length && (
                    <TextTiny lighter>
                        {selectedIds.length}{' '}
                        {pluralize('training', selectedIds.length)} selected (
                        {selectedCount} {pluralize('user', selectedCount)})
                    </TextTiny>
                )}

            <RecommendationTrainingGroupUsersModal
                open={usersModalOpen}
                onClose={() => setUsersModalOpen(false)}
                loading={usersLoading}
                group={usersGroup}
                threshold={data?.threshold}
            />

            <RecommendationTrainingConfigModal
                open={configModalOpen}
                onClose={() => setConfigModalOpen(false)}
                initial={effectiveConfigDraft}
                title="Configure recommendation training"
                subtitle="Override applies to all trainings created from this assessment recommendation page."
                saveLabel="Apply"
                onSave={(payload) => {
                    setConfigOverride(payload);
                    setConfigModalOpen(false);
                    commonStore.success(
                        'Training settings override applied for this page.'
                    );
                }}
            />
        </FlexDiv>
    );
}

export default observer(RecommendationTrainingGroups);
